import { trackMetric } from './Telemetry.js'
import type { VizChartType, VizOpenTrigger } from './OrderTelemetry.js'

/**
 * Visualisation-workspace telemetry. The workspace can be order- or project-scoped, so these do NOT
 * carry order role — only the automatic org bundle (org_id, org_type, is_internal). Kept decoupled
 * from the viz slice types (chart kinds passed as plain strings) on purpose.
 */

/**
 * A visualisation surface opened on a **project** (cross-order) scope, recorded when the route
 * mounts. Projects carry no lab/customer role, so this emits `project_id` where the order variant
 * emits `order_id`/`role` — group `viz.opened` by `scope` to keep the two apart.
 *
 * Without this, every project route (`/projects/:id/otus`,
 * `/project/workspace/:id`) was invisible: `viz.opened` only ever fired from the *order* context
 * menu, so cross-order analysis — the entire point of evaluation projects — recorded nothing.
 */
export const trackProjectVizOpened = (chartType: VizChartType, projectId: string): void => {
  trackMetric('viz.opened', {
    chart_type: chartType,
    scope: 'project',
    trigger: 'navigation' satisfies VizOpenTrigger,
    project_id: projectId
  })
}

/** A chart (sink) added to the workspace. `chart_kind` e.g. stackedBar/pieChart/map/upset/… */
export const trackVizChart = (chartKind: string, isKpi: boolean): void => {
  trackMetric('viz.chart', { chart_kind: chartKind, action: 'add', is_kpi: isKpi })
}

/** Use of a workspace menu (currently the save/load "Views" menu). */
export const trackVizMenu = (action: 'open_save_load'): void => {
  trackMetric('viz.menu', { action })
}

/** A source knob toggle. `knob` e.g. blast_source/count_mode/scaling; `value` its new setting. */
export const trackVizSourceToggle = (knob: string, value: string): void => {
  trackMetric('viz.source_toggle', { knob, value })
}

/**
 * Outcome of a Krona chart mount. `stage` says how far it got, so a stuck chart is distinguishable
 * from a chart that never started streaming. Watch the error rate here rather than inferring it from
 * the absence of `viz.opened`.
 */
export const trackKronaMount = (outcome: 'success' | 'error', stage: string, rowsReceived: number): void => {
  trackMetric('viz.krona_mount', { outcome, stage, rows_received: rowsReceived })
}

/** A workspace mode change. Currently the Nature-KPI mode (enable/disable/apply). */
export const trackVizMode = (mode: string, action: string, extra: Record<string, string | number | boolean> = {}): void => {
  trackMetric('viz.mode', { mode, action, ...extra })
}
